/**
 * Wizard phases — the four high-level groupings shown in the
 * `OnboardingPhaseStepper`. Each phase contains one or more "sub-steps"
 * (logical user-facing screens). One sub-step can be served by more than one
 * `OnboardingScreen` if multiple screens share the same progress position;
 * sub-steps are modeled as `WizardScreen[]` groups rather than flat screens
 * so this remains expressible.
 *
 * The two pre-onboarding screens (`EMAIL_VERIFY`, `ACCOUNT_INFO`) live in
 * `PreOnboardingScreen` because they aren't part of the `OnboardingScreen`
 * enum — they're shown before the proxy-state-driven wizard takes over — but
 * visually still belong to the "About You" phase.
 */

import { OnboardingScreen } from './screens';

export enum WizardPhase {
  ABOUT_YOU = 'ABOUT_YOU',
  LOVED_ONES = 'LOVED_ONES',
  DETAILS = 'DETAILS',
  CONNECT_ACCOUNTS = 'CONNECT_ACCOUNTS',
}

export const WIZARD_PHASES: readonly WizardPhase[] = [
  WizardPhase.ABOUT_YOU,
  WizardPhase.LOVED_ONES,
  WizardPhase.CONNECT_ACCOUNTS,
  WizardPhase.DETAILS,
] as const;

export const WIZARD_PHASE_LABELS: Record<WizardPhase, string> = {
  [WizardPhase.ABOUT_YOU]: 'About You',
  [WizardPhase.LOVED_ONES]: 'Your Loved One(s)',
  [WizardPhase.DETAILS]: 'Membership',
  [WizardPhase.CONNECT_ACCOUNTS]: 'Connect Accounts',
};

export enum PreOnboardingScreen {
  EMAIL_VERIFY = 'EMAIL_VERIFY',
  ACCOUNT_INFO = 'ACCOUNT_INFO',
}

export type WizardScreen = PreOnboardingScreen | OnboardingScreen;

/**
 * Ordered sub-steps for each phase. Each sub-step is a group of one or more
 * `WizardScreen` values that share the same progress position.
 */
const PHASE_SUB_STEPS: Record<WizardPhase, readonly (readonly WizardScreen[])[]> = {
  [WizardPhase.ABOUT_YOU]: [
    [PreOnboardingScreen.ACCOUNT_INFO],
    [PreOnboardingScreen.EMAIL_VERIFY],
    [OnboardingScreen.PHONE_NUMBER, OnboardingScreen.PHONE_NUMBER_VERIFY],
    [OnboardingScreen.USER_RESIDENCE],
    [OnboardingScreen.GREAT_START],
  ],
  [WizardPhase.LOVED_ONES]: [
    [OnboardingScreen.DEPENDENT_COUNT, OnboardingScreen.DEPENDENT_ENTRY, OnboardingScreen.DEPENDENT_REVIEW],
    [OnboardingScreen.DEPENDENT_RESIDENCE],
    [OnboardingScreen.DEPENDENT_VEHICLE],
  ],
  [WizardPhase.CONNECT_ACCOUNTS]: [[OnboardingScreen.ALMOST_THERE], [OnboardingScreen.FINANCIAL_ACCT_CONNECT]],
  [WizardPhase.DETAILS]: [[OnboardingScreen.PAYMENT]],
};

export interface IWizardPhaseLocation {
  phase: WizardPhase;
  phaseIndex: number;
  subStepIndex: number;
  subStepCount: number;
}

/**
 * Locate a screen within the phase taxonomy. Returns the owning phase, its
 * index in `WIZARD_PHASES`, and the screen's position within that phase's
 * sub-step list (`subStepIndex` is 0-based; `subStepCount` is the phase's
 * total). Multiple screens can share a sub-step index when the design groups
 * them under a single progress position. Throws if the screen is unknown.
 */
export function getWizardPhaseLocation(screen: WizardScreen): IWizardPhaseLocation {
  for (let phaseIndex = 0; phaseIndex < WIZARD_PHASES.length; phaseIndex++) {
    const phase = WIZARD_PHASES[phaseIndex];
    const subSteps = PHASE_SUB_STEPS[phase];
    const subStepIndex = subSteps.findIndex((group) => group.includes(screen));
    if (subStepIndex !== -1) {
      return { phase, phaseIndex, subStepIndex, subStepCount: subSteps.length };
    }
  }
  throw new Error(`Unknown wizard screen: ${String(screen)}`);
}

export interface IWizardProgressState {
  /**
   * Index into `WIZARD_PHASES` of the active phase. For `null` (fully
   * onboarded) this is `WIZARD_PHASES.length` — past every phase, no current
   * focus ring.
   */
  activePhaseIndex: number;
  /**
   * Fill ratio (0..1) for each connector line between consecutive phase dots.
   * Index 0 is the line from phase 0 to phase 1. A phase's outgoing line is
   * `subStepIndex / subStepCount`, so single-step phases never show partial
   * fill: their line is 0 while on that phase and 1 once advanced.
   */
  lineFillRatios: readonly number[];
}

/**
 * Compute the render state for `OnboardingPhaseStepper`: which phase carries
 * the current-step focus ring and how full each connector line should be.
 * Pass `null` for a fully-onboarded user — every line fills to 100% and no
 * phase is marked current.
 */
export function getWizardProgressState(screen: WizardScreen | null): IWizardProgressState {
  const lineCount = WIZARD_PHASES.length - 1;
  if (screen === null) {
    return {
      activePhaseIndex: WIZARD_PHASES.length,
      lineFillRatios: Array.from({ length: lineCount }, () => 1),
    };
  }
  const { phaseIndex, subStepIndex, subStepCount } = getWizardPhaseLocation(screen);
  const lineFillRatios = Array.from({ length: lineCount }, (_, i) => {
    if (i < phaseIndex) {
      return 1;
    }
    if (i === phaseIndex) {
      return subStepIndex / subStepCount;
    }
    return 0;
  });
  return { activePhaseIndex: phaseIndex, lineFillRatios };
}
